import { useQuery } from "@apollo/client/react";
import { useParams } from "react-router-dom";
import { EditorHeader } from "../../../components/EditorComponents/MonacoEditorHeader";
import { GqlError } from "../../../components/common/GqlError";
import { COMPILATION_RESULT_QUERY, EDITOR_QUERY } from "../../../graphql";

import { SMALL_SCREEN_WIDTH } from "../../../constants/layout";

import type {
  Credential,
  QueryCompilationResultQuery,
  QueryCompilationResultQueryVariables,
  QueryFullEditorPageQuery,
  QueryFullEditorPageQueryVariables,
} from "../../../graphql/types";

import { Dialog, Grid, Slide, dialogClasses, styled } from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import { Ref, forwardRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { hasPermission } from "../../../components";
import EditorTabs from "../../../components/EditorComponents/EditorTabs";
import { MonacoCodeEditor } from "../../../components/EditorComponents/MonacoCodeEditor";
import EditorPanels from "../../../components/EditorPanels/EditorPanels";
import { SimpleBackdrop } from "../../../components/ui/Backdrop";
import { EmptyContainer } from "../../../components/ui/EmptyContainer";
import DraftStatus from "../../../constants/draftStatus";
import Permissions from "../../../constants/permissions";
import useAuth from "../../../context/UserContext";
import EditorStoreProvider, { useEditorStoreContext } from "../../../store/editor/EditorStoreProvider";
import {
  EditorTabVariant,
  OpenFile,
  buildCompilationResultMap,
  selectErrorsAndWarningsFromAllFiles,
} from "../../../store/editor/editorStore";
import ProblemsSection from "../components/ProblemsSection";

const DialogTransition = forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

type ChangelistCredential = Pick<Credential, "__typename" | "status" | "credential_type" | "name">;

const EditorLoading = () => (
  <SimpleBackdrop open={true} text="Loading Draft...">
    <EmptyContainer />
  </SimpleBackdrop>
);

const PageContainer = styled("div")({
  width: "90vw",
  height: "90vh",
});

const EditorContent = () => {
  const [showProblems, setShowProblems] = useState(true);
  const { user } = useAuth();
  const [errors, warnings, beta] = useEditorStoreContext(selectErrorsAndWarningsFromAllFiles());

  const canSeeBetaMessages = hasPermission(Permissions.BetaMessages, user);
  const hasProblems = errors || warnings || (beta && canSeeBetaMessages);
  const panelHeight = showProblems && hasProblems ? 25 : 5;
  return (
    <>
      <MonacoCodeEditor height="100%" width="100%" panelHeight={panelHeight} />
      {hasProblems ? (
        <Grid size={12} sx={{ height: `${panelHeight}vh` }}>
          <ProblemsSection showProblems={showProblems} setShowProblems={setShowProblems} />
        </Grid>
      ) : null}
    </>
  );
};

const MonacoEditorPage = () => {
  const { version_id: changelistId, file_path } = useParams();

  const {
    loading: loadingCompilation,
    error: errorCompilation,
    data: dataCompilationCurrent,
    previousData: dataCompilationPrevious,
  } = useQuery<QueryCompilationResultQuery, QueryCompilationResultQueryVariables>(COMPILATION_RESULT_QUERY, {
    variables: {
      id: changelistId,
    },
    errorPolicy: "all",
    fetchPolicy: "no-cache",
  });

  const {
    loading: loadingChangelist,
    error: errorChangelist,
    data: dataChangelistCurrent,
    previousData: dataChangelistPrevious,
  } = useQuery<QueryFullEditorPageQuery, QueryFullEditorPageQueryVariables>(EDITOR_QUERY, {
    variables: { id: changelistId, first: 10000 },
    errorPolicy: "all",
    fetchPolicy: "cache-and-network",
  });

  // Fall back to the last good result so a refetch (e.g. on reconnect) that transiently clears
  // data doesn't unmount the editor and lose unsaved edits. Loading/error only apply on first load.
  const dataCompilation = dataCompilationCurrent ?? dataCompilationPrevious;
  const dataChangelist = dataChangelistCurrent ?? dataChangelistPrevious;

  if (!dataCompilation || !dataChangelist || !dataChangelist.changelist) {
    if (loadingCompilation || loadingChangelist) {
      return <EditorLoading />;
    }
    return <GqlError error={[errorCompilation, errorChangelist]} />;
  }

  const changelist = dataChangelist.changelist as NonNullable<(typeof dataChangelist)["changelist"]>;
  const files = changelist.all_files as NonNullable<NonNullable<(typeof changelist)["all_files"][0]>[]>;

  const keys = dataChangelist.credentials?.edges.reduce((v: ChangelistCredential[], edge) => {
    v.push(edge.node);
    return v;
  }, [] as ChangelistCredential[]);

  const compilationChangelist = dataCompilation.changelist as NonNullable<(typeof dataCompilation)["changelist"]>;

  const flatCompilationResult = compilationChangelist.compilation_result as NonNullable<
    (typeof compilationChangelist)["compilation_result"]
  >;

  const openFile: OpenFile | null =
    file_path && files.find((f) => f.path === file_path)
      ? {
          id: uuidv4(),
          path: file_path,
          variant: EditorTabVariant.Editor,
          type: "file",
        }
      : null;
  return (
    <PageContainer>
      <EditorStoreProvider
        id={changelistId || ""}
        files={files}
        openFiles={openFile ? [openFile] : []}
        currentOpenFile={openFile}
        status={changelist.status as DraftStatus}
        compilationResult={buildCompilationResultMap(files, flatCompilationResult)}
      >
        <Dialog
          fullScreen
          open={true}
          disableEnforceFocus
          slots={{ transition: DialogTransition }}
          sx={{
            minWidth: SMALL_SCREEN_WIDTH,
            [`& .${dialogClasses.paper}`]: {
              backgroundImage: "none",
              backgroundColor: "text0.main",
            },
          }}
        >
          <Grid container sx={{ width: "100%", height: "100%" }}>
            <Grid
              size={12}
              sx={(theme) => ({ height: "50px", borderBottom: `0.5px solid ${theme.palette.surfaceDark30.main}` })}
            >
              <EditorHeader
                description={dataChangelist.changelist.description}
                needsMerge={Boolean(dataChangelist.changelist.needs_merge)}
                flowSummary={dataChangelist.changelist?.flow_summary}
              />
            </Grid>
            <Grid container sx={{ width: "100%", height: "calc(100% - 50px)" }}>
              <Grid size={3}>
                <EditorPanels keys={keys} />
              </Grid>
              <Grid container size={9} sx={{ flexGrow: 1 }}>
                <EditorTabs />
                <EditorContent />
              </Grid>
            </Grid>
          </Grid>
        </Dialog>
      </EditorStoreProvider>
    </PageContainer>
  );
};

export default MonacoEditorPage;
